import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import { useReactToPrint } from "react-to-print";
import { Plus, Minus, Trash2, Search, Banknote, ScanLine, Printer, FileText, Coins, Mail, X, ArrowRight } from "lucide-react";
import ZReportDialog from "./ZReportDialog";
import OpenShiftDialog from "./OpenShiftDialog";
import { useAuth } from "../../context/AuthContext";
import { api, peso, formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import BarcodeScanner from "../../components/BarcodeScanner";

const CATS = ["All", "Groceries", "Meats", "Condiments", "Frozen Products", "Sunjin Feeds"];

// ---- Thermal-printer text helpers ---------------------------------------
// 80mm thermal paper at 12pt Courier ≈ 32 character columns. We render the
// receipt body inside a <pre> block so layout is purely space-padded text,
// which is what RawBT + Officom OC-T80A handle reliably (flexbox + Unicode
// peso symbol previously produced garbled output on the live printer).
const RCOLS = 32;
// Peso symbol "₱" is missing from many thermal-printer code pages and prints
// as a glyph-noise blob. Use ASCII "P" prefix on the printed copy.
const pesoTxt = (n) => {
  const v = Number(n || 0);
  return `P${v.toLocaleString("en-PH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
const rcenter = (s) => {
  const t = String(s ?? "");
  if (t.length >= RCOLS) return t.slice(0, RCOLS);
  const pad = Math.floor((RCOLS - t.length) / 2);
  return " ".repeat(pad) + t;
};
const rrule = "-".repeat(RCOLS);
// "2x Pork Belly Lean        P358.00" — left text + right amount, padded to
// RCOLS. If the left text is too long it wraps to a continuation line.
const rrow = (left, right) => {
  const l = String(left ?? "");
  const r = String(right ?? "");
  const maxL = Math.max(1, RCOLS - r.length - 1);
  if (l.length <= maxL) {
    return l + " ".repeat(RCOLS - l.length - r.length) + r;
  }
  const first = l.slice(0, maxL);
  const rest = l.slice(maxL);
  const firstLine = first + " ".repeat(RCOLS - first.length - r.length) + r;
  // Wrap the continuation onto its own line indented by 2 spaces, in chunks
  // of (RCOLS - 2) chars so very long product names don't overflow.
  const contChunks = [];
  let cursor = 0;
  while (cursor < rest.length) {
    contChunks.push("  " + rest.slice(cursor, cursor + (RCOLS - 2)));
    cursor += (RCOLS - 2);
  }
  return [firstLine, ...contChunks].join("\n");
};
// Build the printable text body for a completed sale receipt.
const buildReceiptText = (receipt, cashierInitials) => {
  const lines = [];
  lines.push(rcenter("Felix Taghap Street"));
  lines.push(rcenter("Poblacion 2, Oroquieta City"));
  lines.push(rcenter("7207"));
  lines.push(rcenter("0920 297 1717"));
  lines.push(rcenter("0920 279 9298"));
  lines.push(rrule);
  lines.push(rcenter(receipt.number));
  lines.push(rcenter(new Date(receipt.created_at).toLocaleString("en-PH")));
  lines.push(rcenter("Cashier: " + cashierInitials));
  lines.push(rrule);
  receipt.items.forEach((it) => {
    lines.push(rrow(`${it.quantity}x ${it.name}`, pesoTxt(it.price * it.quantity)));
  });
  lines.push(rrule);
  lines.push(rrow("TOTAL", pesoTxt(receipt.total)));
  const pm = (receipt.payment_method || "").toString();
  lines.push(rrow("Payment", pm.charAt(0).toUpperCase() + pm.slice(1)));
  if (receipt.amount_tendered !== null && receipt.amount_tendered !== undefined) {
    lines.push(rrow("Tendered", pesoTxt(receipt.amount_tendered)));
    lines.push(rrow("Change", pesoTxt(receipt.change || 0)));
  }
  lines.push(rrule);
  lines.push(rcenter("Salamat po!"));
  lines.push(rcenter("Thank you for shopping"));
  lines.push(rcenter("arbeminimart.com"));
  return lines.join("\n");
};

// "Karen Joy Maquiling" → "KJM", "Anna" → "A". Empty/missing → "—".
const initials = (full) => {
  if (!full) return "—";
  return full.trim().split(/\s+/).map((w) => w[0].toUpperCase()).join("").slice(0, 3);
};

// Shown for the first 7 days after a staff member first opens POS, unless
// dismissed. Per-user dismissal so two cashiers sharing a tablet each see
// their own reminder once.
const REMINDER_DAYS = 7;
const PrinterReminderBanner = ({ userId }) => {
  const firstKey = userId ? `arbe_pos_first_seen_${userId}` : null;
  const dismissKey = userId ? `arbe_pos_printer_reminder_dismissed_${userId}` : null;
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (!firstKey || !dismissKey) return;
    try {
      if (localStorage.getItem(dismissKey) === "1") return;
      let first = Number(localStorage.getItem(firstKey));
      if (!first) {
        first = Date.now();
        localStorage.setItem(firstKey, String(first));
      }
      const ageMs = Date.now() - first;
      if (ageMs < REMINDER_DAYS * 24 * 60 * 60 * 1000) setVisible(true);
    } catch { /* private-mode → just hide */ }
  }, [firstKey, dismissKey]);
  const dismiss = () => {
    try { if (dismissKey) localStorage.setItem(dismissKey, "1"); } catch { /* ignore */ }
    setVisible(false);
  };
  const openTab = () => {
    window.dispatchEvent(new CustomEvent("arbe:switch-tab", { detail: "printer" }));
  };
  if (!visible) return null;
  return (
    <div className="flex items-center gap-2 bg-[#FFF8E1] border border-[#E67E22] rounded-lg px-3 py-2 text-xs sm:text-sm text-[#7A4F1A]" data-testid="printer-reminder-banner">
      <Printer className="h-4 w-4 text-[#E67E22] flex-shrink-0" />
      <span className="flex-1 truncate">
        <b>Printer not connected?</b> Follow the one-time setup for the Officom OC-T80A.
      </span>
      <button
        type="button"
        onClick={openTab}
        className="flex-shrink-0 inline-flex items-center gap-1 px-2.5 py-1 rounded bg-[#E67E22] hover:bg-[#B86316] text-white font-semibold text-[11px] sm:text-xs"
        data-testid="printer-reminder-open-btn"
      >
        Open Setup <ArrowRight className="h-3 w-3" />
      </button>
      <button
        type="button"
        onClick={dismiss}
        className="flex-shrink-0 p-1 rounded hover:bg-[#FFE4B5]"
        aria-label="Dismiss"
        data-testid="printer-reminder-dismiss"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
};

export default function AdminPOS() {
  const { user } = useAuth();
  const isCashier = user?.role === "cashier";
  const [shift, setShift] = useState(null);   // { id, opening_cash, opened_at, ... } or null
  const [shiftLoaded, setShiftLoaded] = useState(false);
  const [products, setProducts] = useState([]);
  const [cat, setCat] = useState("All");
  const [q, setQ] = useState("");
  const [cart, setCart] = useState([]); // [{id,name,price,image_url,quantity,stock,unit,note,priceOverride}]
  const [method, setMethod] = useState("cash");
  const [tendered, setTendered] = useState("");
  const [receipt, setReceipt] = useState(null);
  const [emailMode, setEmailMode] = useState(false);   // showing the email input?
  const [emailAddr, setEmailAddr] = useState("");
  const [emailSending, setEmailSending] = useState(false);

  // react-to-print clones the target node into a sandboxed iframe and prints
  // ONLY that iframe — fixes the "prints 2 pages on A4" bug we used to get
  // with @media-print visibility:hidden (the body still occupied paginated space).
  const receiptPrintRef = useRef(null);
  const drawerPrintRef = useRef(null);

  // Receipt CSS used inside the iframe. Length = "auto" by default but every
  // print handler below replaces this with an exact `size: 80mm <Hmm>mm` rule
  // computed from the cloned receipt's measured height, so the print preview
  // shows EXACTLY the paper needed (no wasted whitespace).
  //
  // Thermal-printer-safe rules (lessons from the RawBT/Officom OC-T80A bug
  // where flex rows + 10pt Courier rendered as noise):
  //   - Use <pre> with `white-space: pre` so layout is space-padded text,
  //     NOT CSS flex — the Android print → PDF → RawBT bitmap pipeline
  //     downsamples flex children unevenly at 203 DPI and corrupts glyphs.
  //   - 12pt min, bold weight: gives clean bitmaps at the printer's native DPI.
  //   - Plain ASCII (no `₱`, no `·`, no em-dashes) — Unicode glyphs missing
  //     from the printer's code page came out as the garbled symbols the
  //     customer photographed.
  const RECEIPT_CSS = `
    html, body { margin: 0; padding: 0; background: #fff; }
    .pos-print-receipt {
      display: block !important;
      position: static !important;
      visibility: visible !important;
      left: auto !important;
      width: 80mm;
      padding: 2mm 2mm 4mm 2mm;
      font-family: 'Courier New', Courier, monospace;
      font-size: 12pt;
      font-weight: 700;
      line-height: 1.25;
      color: #000;
      box-sizing: border-box;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    .pos-print-receipt .pr-h1 {
      font-size: 18pt;
      font-weight: 900;
      text-align: center;
      line-height: 1.1;
      margin: 0 0 1mm 0;
      letter-spacing: 0;
    }
    .pos-print-receipt .pr-h2 {
      font-size: 13pt;
      font-weight: 900;
      text-align: center;
      margin: 0;
    }
    .pos-print-receipt pre.pr-body {
      margin: 0;
      padding: 0;
      font-family: inherit;
      font-size: inherit;
      font-weight: inherit;
      line-height: inherit;
      white-space: pre;
      color: #000;
    }
  `;
  // 1 CSS pixel = 0.264583 mm at 96 DPI (browser print baseline).
  const PX_TO_MM = 0.264583;
  // Build a `@page { size: 80mm Hmm; margin:0; }` rule from the live source
  // node's scrollHeight. Adds 4mm safety so the last printed line isn't clipped.
  const dynamicPageStyle = (ref) => () => {
    const node = ref.current;
    const hPx = node ? node.scrollHeight : 0;
    const hMm = Math.max(40, Math.ceil(hPx * PX_TO_MM) + 4);
    return `@page { size: 80mm ${hMm}mm; margin: 0; }\n${RECEIPT_CSS}`;
  };

  const printReceipt = useReactToPrint({
    contentRef: receiptPrintRef,
    documentTitle: receipt ? `Arbe-${receipt.number}` : "Arbe-Receipt",
    pageStyle: dynamicPageStyle(receiptPrintRef),
  });
  const printDrawerKick = useReactToPrint({
    contentRef: drawerPrintRef,
    documentTitle: "Arbe-DrawerOpen",
    pageStyle: dynamicPageStyle(drawerPrintRef),
  });

  // Reset receipt-dialog state every time a new sale opens.
  useEffect(() => {
    if (!receipt) { setEmailMode(false); setEmailAddr(""); }
  }, [receipt]);
  const [zOpen, setZOpen] = useState(false);
  const [xOpen, setXOpen] = useState(false);
  // "Open Cash Drawer" — fires a minimal print job. RawBT's setting
  // "Open cash drawer before printing" sends the ESC/POS kick pulse to the
  // Officom OC-T80A so the drawer pops open without ringing up a sale.
  const [drawerKick, setDrawerKick] = useState(false);
  useEffect(() => {
    if (!drawerKick) return;
    const t = setTimeout(() => {
      try { printDrawerKick(); } catch { /* user cancelled */ }
      setDrawerKick(false);
    }, 80);
    return () => clearTimeout(t);
  }, [drawerKick, printDrawerKick]);
  const openDrawer = () => {
    toast.info("Opening cash drawer…", { duration: 2500 });
    setDrawerKick(true);
  };

  const [scanOpen, setScanOpen] = useState(false);

  useEffect(() => {
    api.get("/products/all").then((r) => setProducts(r.data));
  }, []);

  // Load the cashier's current open shift (managers/admins always get null
  // so the open-shift modal stays hidden for management roles).
  const loadShift = useCallback(async () => {
    try {
      const { data } = await api.get("/pos/shift/current");
      setShift(data);
    } catch {
      setShift(null);
    } finally {
      setShiftLoaded(true);
    }
  }, []);
  useEffect(() => { loadShift(); }, [loadShift]);
  // Refresh shift totals every 30s so the status bar stays live.
  useEffect(() => {
    if (!shift || !isCashier) return;
    const t = setInterval(loadShift, 30000);
    return () => clearInterval(t);
  }, [shift, isCashier, loadShift]);

  const filtered = useMemo(() => {
    return products.filter((p) =>
      (cat === "All" || p.category === cat) &&
      (q === "" || p.name.toLowerCase().includes(q.toLowerCase())) &&
      p.active
    );
  }, [products, cat, q]);

  const add = (p) => {
    setCart((prev) => {
      const f = prev.find((i) => i.id === p.id && !i.priceOverride);
      if (f) {
        if (f.quantity >= p.stock) { toast.error("Not enough stock"); return prev; }
        return prev.map((i) => i === f ? { ...i, quantity: i.quantity + 1 } : i);
      }
      if (p.stock <= 0) { toast.error("Out of stock"); return prev; }
      return [...prev, { id: p.id, name: p.name, price: p.price, image_url: p.image_url, stock: p.stock, unit: p.unit, quantity: 1 }];
    });
  };

  const addScanned = ({ product, unit_price, type, raw_code }) => {
    setCart((prev) => [
      ...prev,
      {
        id: product.id,
        name: product.name,
        price: unit_price,
        image_url: product.image_url,
        stock: product.stock,
        unit: product.unit,
        quantity: 1,
        priceOverride: type === "scale" ? unit_price : undefined,
        note: type === "scale" ? `Scale label ${raw_code}` : undefined,
        lineKey: `${product.id}-${raw_code}`,
      },
    ]);
    toast.success(`Added ${product.name} — ${peso(unit_price)}${type === "scale" ? " (from scale)" : ""}`);
  };

  const handleScan = async (code) => {
    setScanOpen(false);
    try {
      const { data } = await api.get(`/pos/resolve-barcode?code=${encodeURIComponent(code)}`);
      addScanned(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const dec = (idx) => setCart((p) => p.flatMap((i, k) => k === idx ? (i.quantity <= 1 ? [] : [{ ...i, quantity: i.quantity - 1 }]) : [i]));
  const inc = (idx) => setCart((p) => p.map((i, k) => k === idx ? { ...i, quantity: Math.min(i.quantity + 1, i.stock) } : i));
  const rm  = (idx) => setCart((p) => p.filter((_, k) => k !== idx));

  const subtotal = cart.reduce((s, i) => s + i.price * i.quantity, 0);
  const change = method === "cash" && tendered ? Number(tendered) - subtotal : null;

  const checkout = async () => {
    if (cart.length === 0) { toast.error("Cart empty"); return; }
    if (method === "cash" && tendered && Number(tendered) < subtotal) { toast.error("Insufficient cash"); return; }
    try {
      const payload = {
        items: cart.map((i) => ({
          product_id: i.id,
          quantity: i.quantity,
          unit_price_override: i.priceOverride ?? null,
          note: i.note ?? null,
        })),
        payment_method: method,
        amount_tendered: tendered ? Number(tendered) : null,
      };
      const { data } = await api.post("/pos/sale", payload);
      // Always show the Sale Complete dialog — cashier picks Email / Print / No receipt.
      setReceipt(data);
      setCart([]); setTendered("");
      // reload stock
      api.get("/products/all").then((r) => setProducts(r.data));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="grid lg:grid-cols-5 gap-4">
      {/* Products */}
      <div className="lg:col-span-3 space-y-3 order-2 lg:order-1">
        <PrinterReminderBanner userId={user?.id} />
        {isCashier && shift && (
          <div className="flex items-center justify-between gap-2 bg-[#E8F5E8] border border-[#2F5233] rounded-lg px-3 py-2 text-xs sm:text-sm" data-testid="shift-status-bar">
            <div className="flex items-center gap-2 min-w-0">
              <Coins className="h-4 w-4 text-[#2F5233] flex-shrink-0" />
              <span className="font-semibold text-[#1F3D24] truncate">Shift open</span>
              <span className="text-[#4A5D5A] truncate">· Opening {peso(shift.opening_cash)}</span>
              <span className="text-[#4A5D5A] hidden sm:inline truncate">· {new Date(shift.opened_at).toLocaleTimeString("en-PH", { hour: "2-digit", minute: "2-digit" })}</span>
            </div>
            {shift.live && (
              <div className="flex-shrink-0 text-[#2F5233] font-semibold" data-testid="shift-status-sales">
                {shift.live.transactions} txn · {peso(shift.live.sales)}
              </div>
            )}
          </div>
        )}
        <div className="flex gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[180px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#738380]" />
            <Input placeholder="Search product..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-10 h-11 bg-white" data-testid="pos-search" />
          </div>
          <Select value={cat} onValueChange={setCat}>
            <SelectTrigger className="w-32 sm:w-40 h-11 bg-white"><SelectValue /></SelectTrigger>
            <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
          <Button
            type="button"
            onClick={() => setScanOpen(true)}
            className="h-11 bg-[#2F5233] hover:bg-[#1F3D24] text-white rounded-lg"
            data-testid="pos-scan-btn"
          >
            <ScanLine className="h-5 w-5 sm:mr-2" /> <span className="hidden sm:inline">Scan</span>
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={openDrawer}
            className="h-11 rounded-lg border-[#E67E22] text-[#E67E22] hover:bg-[#E67E22] hover:text-white"
            data-testid="pos-open-drawer-btn"
            title="No-sale: open the cash drawer via the receipt printer"
          >
            <Coins className="h-5 w-5 sm:mr-2" /> <span className="hidden sm:inline">Open Drawer</span>
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => setXOpen(true)}
            className="h-11 rounded-lg border-[#5B7553] text-[#2F5233] hover:bg-[#2F5233] hover:text-white"
            data-testid="pos-x-report-btn"
            title="Mid-shift snapshot — does not close the shift"
          >
            <FileText className="h-5 w-5 sm:mr-2" /> <span className="hidden sm:inline">X-Report</span>
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => setZOpen(true)}
            className="h-11 rounded-lg border-[#1C2826] text-[#1C2826] hover:bg-[#1C2826] hover:text-white"
            data-testid="pos-close-shift-btn"
          >
            <FileText className="h-5 w-5 sm:mr-2" /> <span className="hidden sm:inline">Close Shift</span>
          </Button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-h-[52vh] sm:max-h-[68vh] overflow-y-auto pr-1">
          {filtered.map((p) => (
            <button
              key={p.id}
              onClick={() => add(p)}
              disabled={p.stock <= 0}
              className="text-left bg-white border border-[#E1E5DF] rounded-lg p-3 hover:border-[#2F5233] transition disabled:opacity-40"
              data-testid={`pos-product-${p.id}`}
            >
              <img src={p.image_url} alt="" className="h-24 w-full object-cover rounded bg-[#F1F3EE]" />
              <div className="mt-2 font-semibold text-sm line-clamp-2 text-[#1C2826]">{p.name}</div>
              <div className="mt-1 flex justify-between items-center">
                <span className="font-heading font-bold text-[#2F5233]">{peso(p.price)}</span>
                <span className="text-xs text-[#738380]">Stock: {p.stock}</span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Cart */}
      <div className="lg:col-span-2 bg-white border border-[#E1E5DF] rounded-lg p-4 flex flex-col order-1 lg:order-2 lg:sticky lg:top-24 lg:self-start">
        <div className="flex items-center justify-between mb-3">
          <div className="font-heading font-bold text-lg text-[#1C2826]">Walk-in sale</div>
          <Banknote className="h-5 w-5 text-[#E67E22]" />
        </div>
        <div className="flex-1 overflow-y-auto space-y-2 max-h-[48vh]">
          {cart.length === 0 && <div className="text-center text-[#738380] text-sm py-10">Tap products to add, or use the <span className="inline-flex items-center gap-1 font-semibold text-[#2F5233]"><ScanLine className="h-4 w-4" />Scan</span> button for scale labels</div>}
          {cart.map((i, idx) => (
            <div key={i.lineKey || i.id + "-" + idx} className="flex items-center gap-3 border-b border-[#E1E5DF] pb-2" data-testid={`pos-cart-${idx}`}>
              <img src={i.image_url} alt="" className="h-10 w-10 rounded object-cover" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold line-clamp-1">
                  {i.name}
                  {i.priceOverride != null && (
                    <span className="ml-2 text-[9px] font-bold uppercase bg-[#E67E22] text-white px-1.5 py-0.5 rounded tracking-wider">Scale</span>
                  )}
                </div>
                <div className="text-xs text-[#738380]">{peso(i.price)} × {i.quantity} = {peso(i.price * i.quantity)}</div>
              </div>
              <div className="flex items-center border border-[#E1E5DF] rounded-full">
                <button className="h-7 w-7 flex items-center justify-center" onClick={() => dec(idx)}><Minus className="h-3 w-3" /></button>
                <span className="w-7 text-center text-sm">{i.quantity}</span>
                <button className="h-7 w-7 flex items-center justify-center" onClick={() => inc(idx)}><Plus className="h-3 w-3" /></button>
              </div>
              <button onClick={() => rm(idx)} className="text-[#E74C3C]"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>

        <div className="mt-3 pt-3 border-t border-[#E1E5DF] space-y-2">
          <div className="flex justify-between font-heading font-extrabold text-xl">
            <span>Total</span><span className="text-[#2F5233]">{peso(subtotal)}</span>
          </div>
          <div>
            <Label>Payment</Label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger className="mt-1.5" data-testid="pos-method"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="cash">Cash</SelectItem>
                <SelectItem value="gcash">GCash</SelectItem>
                <SelectItem value="maya">Maya</SelectItem>
                <SelectItem value="bank">Bank</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {method === "cash" && (
            <div>
              <Label>Cash tendered</Label>
              <Input
                type="number"
                step="0.01"
                value={tendered}
                onChange={(e) => setTendered(e.target.value)}
                className="mt-1.5 h-11 text-lg font-mono font-bold"
                inputMode="decimal"
                data-testid="pos-tendered"
              />
              {/* Tablet-friendly quick keypad — taps ADD to current tendered. */}
              <div className="grid grid-cols-3 gap-1.5 mt-2" data-testid="pos-quick-keypad">
                {[20, 50, 100, 200, 500, 1000].map((denom) => (
                  <button
                    key={denom}
                    type="button"
                    onClick={() => setTendered((prev) => {
                      const cur = Number(prev) || 0;
                      return String(Math.round((cur + denom) * 100) / 100);
                    })}
                    className="h-12 rounded-lg bg-[#F1F3EE] hover:bg-[#E1E5DF] active:bg-[#BEDDC2] font-heading font-extrabold text-lg text-[#1C2826] transition-colors border border-[#E1E5DF] select-none"
                    data-testid={`pos-keypad-${denom}`}
                  >
                    +₱{denom}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setTendered(String(Math.round(subtotal * 100) / 100))}
                  disabled={subtotal <= 0}
                  className="h-12 rounded-lg bg-[#2F5233] hover:bg-[#1F3D24] disabled:bg-[#BEDDC2] disabled:cursor-not-allowed font-heading font-extrabold text-sm text-white transition-colors col-span-2"
                  data-testid="pos-keypad-exact"
                >
                  Exact ({peso(subtotal)})
                </button>
                <button
                  type="button"
                  onClick={() => setTendered("")}
                  className="h-12 rounded-lg bg-white hover:bg-[#FFE8E8] active:bg-[#FFD5D5] font-heading font-extrabold text-sm text-[#C62828] transition-colors border border-[#E1E5DF]"
                  data-testid="pos-keypad-clear"
                >
                  Clear
                </button>
              </div>
              {tendered && (
                <div className="text-sm mt-2 p-2 rounded-lg bg-[#EAF5EC] border border-[#BEDDC2]">
                  Change: <span className="font-heading font-extrabold text-lg text-[#2F5233]" data-testid="pos-change-display">{peso(change || 0)}</span>
                </div>
              )}
            </div>
          )}
          <Button className="w-full h-11 bg-[#E67E22] hover:bg-[#D35400] text-white rounded-lg font-semibold" onClick={checkout} data-testid="pos-checkout">
            Complete Sale
          </Button>
        </div>
      </div>

      <Dialog open={!!receipt} onOpenChange={(v) => !v && setReceipt(null)}>
        <DialogContent className="max-w-sm">
          {receipt && (
            <>
              <DialogHeader><DialogTitle className="text-center">Sale Complete ✓</DialogTitle></DialogHeader>
              <div className="text-sm font-mono">
                <div className="text-center font-heading text-lg font-bold">ARBE MINIMART</div>
                <div className="text-center text-[11px] text-[#738380]">Felix Taghap Street, Poblacion 2</div>
                <div className="text-center text-[11px] text-[#738380]">Oroquieta City 7207 · 0920 297 1717</div>
                <div className="my-2 border-t border-dashed border-[#E1E5DF]" />
                <div className="text-center text-xs text-[#738380]">{receipt.number}</div>
                <div className="text-center text-xs text-[#738380]">{new Date(receipt.created_at).toLocaleString("en-PH")}</div>
                <div className="text-center text-xs text-[#738380]">Cashier: {initials(receipt.cashier_name)}</div>
                <div className="my-3 border-t border-dashed border-[#E1E5DF]" />
                {receipt.items.map((it, i) => (
                  <div key={`${it.product_id || it.name}-${i}`} className="flex justify-between"><span>{it.quantity}× {it.name}</span><span>{peso(it.price * it.quantity)}</span></div>
                ))}
                <div className="my-2 border-t border-dashed border-[#E1E5DF]" />
                <div className="flex justify-between font-bold"><span>Total</span><span>{peso(receipt.total)}</span></div>
                <div className="flex justify-between"><span>Payment</span><span className="capitalize">{receipt.payment_method}</span></div>
                {receipt.amount_tendered !== null && receipt.amount_tendered !== undefined && (
                  <>
                    <div className="flex justify-between"><span>Tendered</span><span>{peso(receipt.amount_tendered)}</span></div>
                    <div className="flex justify-between"><span>Change</span><span>{peso(receipt.change || 0)}</span></div>
                  </>
                )}
                <div className="text-center text-xs mt-3 text-[#738380]">Salamat po! Thank you for shopping at Arbe Minimart.</div>
              </div>

              {emailMode ? (
                <div className="space-y-2 mt-3" data-testid="pos-email-form">
                  <Label htmlFor="pos-email-input" className="text-xs">Customer email address</Label>
                  <Input
                    id="pos-email-input"
                    type="email"
                    placeholder="customer@example.com"
                    value={emailAddr}
                    onChange={(e) => setEmailAddr(e.target.value)}
                    className="h-11 text-base"
                    autoFocus
                    data-testid="pos-email-input"
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      variant="outline"
                      onClick={() => { setEmailMode(false); setEmailAddr(""); }}
                      disabled={emailSending}
                      className="h-11"
                      data-testid="pos-email-cancel"
                    >
                      <X className="h-4 w-4 mr-1" /> Back
                    </Button>
                    <Button
                      onClick={async () => {
                        if (!emailAddr || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailAddr)) {
                          toast.error("Enter a valid email address"); return;
                        }
                        setEmailSending(true);
                        try {
                          const { data } = await api.post(`/pos/sale/${receipt.id}/email-receipt`, { email: emailAddr });
                          if (data.sent) toast.success(`Receipt emailed to ${emailAddr}`);
                          else toast.warning(`Sale recorded · email skipped (${data.reason || "not configured"})`, { duration: 6000 });
                          setReceipt(null);
                        } catch (e) { toast.error(formatApiError(e)); }
                        finally { setEmailSending(false); }
                      }}
                      disabled={emailSending}
                      className="h-11 bg-[#2F5233] hover:bg-[#1F3D24] text-white font-bold"
                      data-testid="pos-email-send"
                    >
                      <Mail className="h-4 w-4 mr-1" /> {emailSending ? "Sending…" : "Send"}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-2 mt-3" data-testid="pos-receipt-options">
                  <div className="text-xs uppercase tracking-wider font-bold text-[#4A5D5A] text-center">Send receipt as</div>
                  <Button
                    onClick={() => setEmailMode(true)}
                    className="w-full h-12 bg-[#2F5233] hover:bg-[#1F3D24] text-white text-base font-bold"
                    data-testid="pos-receipt-email-btn"
                  >
                    <Mail className="h-5 w-5 mr-2" /> Email receipt
                  </Button>
                  <Button
                    onClick={() => { try { printReceipt(); } catch { /* user cancelled */ } }}
                    className="w-full h-12 bg-[#1C2826] hover:bg-black text-white text-base font-bold"
                    data-testid="pos-print-receipt"
                  >
                    <Printer className="h-5 w-5 mr-2" /> Print receipt
                  </Button>
                  <Button
                    onClick={() => { toast.success(`Sale ${receipt.number} recorded`); setReceipt(null); }}
                    variant="outline"
                    className="w-full h-11 border-[#A0AAA7] text-[#4A5D5A] hover:bg-[#F1F3EE]"
                    data-testid="pos-new-sale"
                  >
                    No receipt — record sale
                  </Button>
                </div>
              )}
              <p className="text-[10px] text-center text-[#738380] mt-2">
                The sale is already recorded. Receipt method is for the customer's copy.
              </p>

              {/* Printable receipt — rendered into a sandboxed iframe by
                  react-to-print, so we keep it always-mounted (hidden on
                  screen via the .pos-print-receipt CSS that has display:none).
                  Body is a <pre> block of space-padded monospace text so the
                  RawBT → Officom OC-T80A bitmap pipeline gets clean glyphs. */}
              <div ref={receiptPrintRef} id="pos-print-area" className="pos-print-receipt">
                <div className="pr-h1">ARBE MINIMART</div>
                <pre className="pr-body">{buildReceiptText(receipt, initials(receipt.cashier_name))}</pre>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
      <BarcodeScanner open={scanOpen} onClose={() => setScanOpen(false)} onResult={handleScan} />
      {zOpen && (
        <ZReportDialog
          open={zOpen}
          onClose={() => { setZOpen(false); loadShift(); }}
          mode="z"
          shift={shift}
          onShiftClosed={(closed) => { setShift(null); toast.success(`Shift closed · Variance ${peso(closed.variance)}`); }}
        />
      )}
      {xOpen && <ZReportDialog open={xOpen} onClose={() => setXOpen(false)} mode="x" shift={shift} />}

      {/* Cashier shift gate — modal cannot be dismissed until shift opens. */}
      {isCashier && shiftLoaded && !shift && (
        <OpenShiftDialog open onOpened={(s) => setShift({ ...s, live: { sales: 0, transactions: 0, cash_sales: 0, payments: [] } })} />
      )}

      {/* Cash-drawer kick: a 1-line print job. RawBT's "Open cash drawer
          before printing" toggle (Settings → Cash Drawer) sends the ESC/POS
          kick pulse to the printer, popping open the drawer. Always mounted
          (hidden via the .pos-print-receipt CSS) so react-to-print's ref
          target stays attached; only the print action is triggered on demand. */}
      <div ref={drawerPrintRef} id="pos-drawer-kick-area" className="pos-print-receipt">
        <div className="pr-h2">*** CASH DRAWER OPEN ***</div>
        <pre className="pr-body">{rcenter(new Date().toLocaleString("en-PH"))}</pre>
      </div>
    </div>
  );
}
